import AnimatedContainer from '@components/primitives/AnimatedContainer/index.client'
import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons/faCheckCircle'
import { faXmarkCircle } from '@fortawesome/pro-regular-svg-icons/faXmarkCircle'
import animationPresets from '@lib/animation/presets'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

export type ToastProps = {
  className?: string
  content: React.ReactNode
  duration?: number
}

export function Toast({
  showToast,
  toastSettings,
}: {
  showToast: Dispatch<SetStateAction<ToastProps | null>>
  toastSettings: ToastProps | null
}) {
  const [isToastVisible, setToastVisible] = useState(false)

  useEffect(() => {
    if (!toastSettings) {
      setToastVisible(false)
      return
    }
    setToastVisible(true)
    const timeout = setTimeout(() => {
      setToastVisible(false)
      showToast(null)
    }, toastSettings.duration ?? 5_000)
    return () => {
      clearTimeout(timeout)
    }
  }, [toastSettings])

  if (!isToastVisible || !toastSettings) {
    return null
  }

  const animationOptions = animationPresets.fadeIn({
    duration: 400,
  })

  return (
    <AnimatedContainer
      animationOptions={animationOptions}
      className={classMerge(
        'fixed bottom-10 right-8 z-toast flex h-[56px] w-4/5 items-center justify-center rounded-md bg-success600 md:w-1/3',
        toastSettings.className,
      )}
    >
      {toastSettings.content}
    </AnimatedContainer>
  )
}

export const CustomToastSuccessMessage = ({
  className,
  iconClassName,
  message,
}: {
  className?: string
  iconClassName?: string
  message: string
}) => (
  <div>
    <Icon
      className={classMerge('text-success100', iconClassName)}
      icon={faCheckCircle}
    />
    <span
      className={classMerge('ml-2 font-extrabold text-success100', className)}
    >
      {message}
    </span>
  </div>
)

export const CustomToastFailureMessage = ({ message }: { message: string }) => (
  <div>
    <Icon className='text-warning100' icon={faXmarkCircle} />
    <span className='ml-2 font-extrabold text-warning100'>{message}</span>
  </div>
)

export const DefaultToastMessages = {
  copy: {
    className: 'bg-success500 text-white',
    content: <CustomToastSuccessMessage message='Copied' />,
  },
  failure: {
    className: 'bg-warm500 text-white',
    content: (
      <div>
        <Icon className='text-warning100' icon={faXmarkCircle} />
        <span className='ml-2 font-extrabold text-warning100'>{'Failure'}</span>
      </div>
    ),
  },
  saveSuccess: {
    className: 'bg-success500 text-white',
    content: <CustomToastSuccessMessage message='Saved Successfully' />,
  },
  success: {
    className: 'bg-success500 text-white',
    content: <CustomToastSuccessMessage message='Success' />,
  },
}
